import { supabase } from './supabase';

const db = supabase as any;

import {
  fetchGoalsForUser, fetchHabitsForUser, fetchTasksForUser,
  fetchCheckInsForUser, fetchMilestonesForUser, fetchJournalEntriesForUser,
  type Goal, type Habit, type Task, type CheckIn, type Milestone, type JournalEntry,
} from './progress';

export type ReportType = 'weekly' | 'monthly' | 'programme_end';

export interface ScoreSnapshot {
  characteristicId: string;
  normalized: number;
}

export interface HarmonicScoreSnapshot {
  levelId: string;
  normalized: number;
}

export interface AssessmentSnapshot {
  version: number;
  completedAt: string;
  scores: ScoreSnapshot[];
  harmonicScores: HarmonicScoreSnapshot[];
  aiAnalysis?: string;
}

export interface CheckInStats {
  count: number;
  avgEnergy: number;
  avgMood: number;
  avgFocus: number;
  trend: Array<{ date: string; avg: number }>;
}

export interface HabitStats {
  title: string;
  targetDaysPerWeek: number;
  completedInPeriod: number;
  totalPossible: number;
  completionRate: number;
}

export interface Quantitative {
  periodStart: string;
  periodEnd: string;
  // Goals
  goalsActive: number;
  goalsCompleted: number;
  goalsPaused: number;
  topActiveGoals: Array<{ title: string; targetDate?: string }>;
  // Habits
  habitStats: HabitStats[];
  overallHabitRate: number;
  // Tasks
  tasksCompleted: number;
  tasksOpen: number;
  taskCompletionRate: number;
  // Check-ins
  checkInStats: CheckInStats;
  // Milestones
  milestonesInPeriod: number;
  milestonesList: Array<{ title: string; achievedAt: string }>;
  // Journal
  journalEntriesInPeriod: number;
  journalExcerpts: Array<{ title: string; date: string; content: string }>;
  // Assessment
  latestAssessment?: AssessmentSnapshot;
  previousAssessment?: AssessmentSnapshot;
  // Pathway
  pathwayCompletionCount: number;
  // Coaching sessions
  sessionCount: number;
  sessionNotes: Array<{ date: string; notes: string; followUps: string }>;
}

export interface TransformationReport {
  id: string;
  respondent_user_id: string;
  coach_user_id: string | null;
  report_type: ReportType;
  period_start: string;
  period_end: string;
  title: string;
  quantitative: Quantitative;
  ai_narrative: string | null;
  coach_notes: string | null;
  is_published: boolean;
  generated_by: 'coach' | 'system';
  created_at: string;
  updated_at: string;
}

function inPeriod(dateStr: string, start: Date, end: Date): boolean {
  const d = new Date(dateStr);
  return d >= start && d <= end;
}

function avg(nums: number[]): number {
  if (!nums.length) return 0;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
}

export async function buildQuantitativeSnapshot(
  userId: string,
  periodStart: Date,
  periodEnd: Date,
): Promise<Quantitative> {
  const [goals, habits, tasks, checkIns, milestones, journals] = await Promise.all([
    fetchGoalsForUser(userId),
    fetchHabitsForUser(userId),
    fetchTasksForUser(userId),
    fetchCheckInsForUser(userId, 90),
    fetchMilestonesForUser(userId),
    fetchJournalEntriesForUser(userId, 50),
  ]);

  // Goals
  const activeGoals = goals.filter((g: Goal) => g.status === 'active');
  const completedGoals = goals.filter(
    (g: Goal) => g.status === 'completed' && g.completed_at && inPeriod(g.completed_at, periodStart, periodEnd),
  );
  const pausedGoals = goals.filter((g: Goal) => g.status === 'paused');

  // Habits
  const periodDays = Math.ceil((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const activeHabits = habits.filter((h: Habit) => h.is_active);
  const habitStats: HabitStats[] = activeHabits.map((h: Habit) => {
    const completionsInPeriod = (h.completions || []).filter((c) =>
      inPeriod(c.completed_date, periodStart, periodEnd),
    ).length;
    const weeksInPeriod = periodDays / 7;
    const totalPossible = Math.round(h.target_days_per_week * weeksInPeriod);
    return {
      title: h.title,
      targetDaysPerWeek: h.target_days_per_week,
      completedInPeriod: completionsInPeriod,
      totalPossible: totalPossible || 1,
      completionRate: totalPossible > 0 ? Math.round((completionsInPeriod / totalPossible) * 100) : 0,
    };
  });
  const overallHabitRate =
    habitStats.length > 0 ? Math.round(avg(habitStats.map((h) => h.completionRate))) : 0;

  // Tasks
  const tasksCompleted = tasks.filter(
    (t: Task) => t.status === 'done' && t.completed_at && inPeriod(t.completed_at, periodStart, periodEnd),
  ).length;
  const tasksOpen = tasks.filter((t: Task) => t.status !== 'done').length;
  const totalTasks = tasks.filter(
    (t: Task) => inPeriod(t.created_at, periodStart, periodEnd),
  ).length;
  const taskCompletionRate = totalTasks > 0 ? Math.round((tasksCompleted / totalTasks) * 100) : 0;

  // Check-ins
  const periodCheckIns = checkIns.filter((c: CheckIn) => inPeriod(c.checked_in_at, periodStart, periodEnd));
  const checkInStats: CheckInStats = {
    count: periodCheckIns.length,
    avgEnergy: avg(periodCheckIns.map((c: CheckIn) => c.energy_level)),
    avgMood: avg(periodCheckIns.map((c: CheckIn) => c.mood_level)),
    avgFocus: avg(periodCheckIns.map((c: CheckIn) => c.focus_level)),
    trend: periodCheckIns.map((c: CheckIn) => ({
      date: c.checked_in_at,
      avg: Math.round(((c.energy_level + c.mood_level + c.focus_level) / 3) * 10) / 10,
    })).reverse(),
  };

  // Milestones
  const periodMilestones = milestones.filter((m: Milestone) => inPeriod(m.achieved_at, periodStart, periodEnd));

  // Journal
  const periodJournals = journals.filter((j: JournalEntry) => inPeriod(j.created_at, periodStart, periodEnd));

  // Assessment snapshots
  const { data: allResults } = await db
    .from('results')
    .select('id, scores, harmonic_scores, completed_at, version, ai_analysis')
    .eq('user_id', userId)
    .order('completed_at', { ascending: false });

  let latestAssessment: AssessmentSnapshot | undefined;
  let previousAssessment: AssessmentSnapshot | undefined;

  if (allResults && allResults.length > 0) {
    const r = allResults[0];
    latestAssessment = {
      version: r.version || 1,
      completedAt: r.completed_at,
      scores: r.scores || [],
      harmonicScores: r.harmonic_scores || [],
      aiAnalysis: r.ai_analysis,
    };
    if (allResults.length > 1) {
      const p = allResults[1];
      previousAssessment = {
        version: p.version || 1,
        completedAt: p.completed_at,
        scores: p.scores || [],
        harmonicScores: p.harmonic_scores || [],
        aiAnalysis: p.ai_analysis,
      };
    }
  }

  // Pathway completions
  const { count: pathwayCount } = await db
    .from('pathway_assignments')
    .select('*', { count: 'exact', head: true })
    .eq('respondent_user_id', userId)
    .eq('status', 'completed');

  // Coaching sessions (in period)
  const { data: sessions } = await db
    .from('coaching_sessions')
    .select('session_date, notes, follow_ups')
    .gte('session_date', periodStart.toISOString().split('T')[0])
    .lte('session_date', periodEnd.toISOString().split('T')[0]);

  const sessionNotes = (sessions || [])
    .filter((s: any) => s.notes || s.follow_ups)
    .map((s: any) => ({ date: s.session_date, notes: s.notes || '', followUps: s.follow_ups || '' }));

  return {
    periodStart: periodStart.toISOString().split('T')[0],
    periodEnd: periodEnd.toISOString().split('T')[0],
    goalsActive: activeGoals.length,
    goalsCompleted: completedGoals.length,
    goalsPaused: pausedGoals.length,
    topActiveGoals: activeGoals.slice(0, 5).map((g: Goal) => ({ title: g.title, targetDate: g.target_date })),
    habitStats,
    overallHabitRate,
    tasksCompleted,
    tasksOpen,
    taskCompletionRate,
    checkInStats,
    milestonesInPeriod: periodMilestones.length,
    milestonesList: periodMilestones.map((m: Milestone) => ({ title: m.title, achievedAt: m.achieved_at })),
    journalEntriesInPeriod: periodJournals.length,
    journalExcerpts: periodJournals.slice(0, 3).map((j: JournalEntry) => ({
      title: j.title,
      date: j.created_at,
      content: j.content.slice(0, 200),
    })),
    latestAssessment,
    previousAssessment,
    pathwayCompletionCount: pathwayCount || 0,
    sessionCount: sessions?.length || 0,
    sessionNotes,
  };
}

export async function createReport(
  coachUserId: string,
  respondentUserId: string,
  reportType: ReportType,
  periodStart: Date,
  periodEnd: Date,
  title: string,
  quantitative: Quantitative,
): Promise<TransformationReport | null> {
  const { data } = await db
    .from('transformation_reports')
    .insert({
      respondent_user_id: respondentUserId,
      coach_user_id: coachUserId,
      report_type: reportType,
      period_start: periodStart.toISOString().split('T')[0],
      period_end: periodEnd.toISOString().split('T')[0],
      title,
      quantitative: quantitative as any,
      generated_by: 'coach',
    })
    .select()
    .single();
  return data as TransformationReport | null;
}

export async function updateReport(
  id: string,
  payload: Partial<Pick<TransformationReport, 'ai_narrative' | 'coach_notes' | 'is_published' | 'title'>>,
): Promise<void> {
  await db
    .from('transformation_reports')
    .update({ ...payload, updated_at: new Date().toISOString() })
    .eq('id', id);
}

export async function fetchReportsForRespondent(
  coachUserId: string,
  respondentUserId: string,
): Promise<TransformationReport[]> {
  const { data } = await db
    .from('transformation_reports')
    .select('*')
    .eq('respondent_user_id', respondentUserId)
    .eq('coach_user_id', coachUserId)
    .order('created_at', { ascending: false });
  return (data || []) as unknown as TransformationReport[];
}

export async function fetchMyReports(userId: string): Promise<TransformationReport[]> {
  const { data } = await db
    .from('transformation_reports')
    .select('*')
    .eq('respondent_user_id', userId)
    .eq('is_published', true)
    .order('created_at', { ascending: false });
  return (data || []) as unknown as TransformationReport[];
}

export async function fetchReportById(id: string): Promise<TransformationReport | null> {
  const { data } = await db
    .from('transformation_reports')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  return data as unknown as TransformationReport | null;
}

export async function deleteReport(id: string): Promise<void> {
  await db.from('transformation_reports').delete().eq('id', id);
}

export interface TransformationReportWithRespondent extends TransformationReport {
  respondent_name: string;
  respondent_email: string;
}

export async function fetchAllReportsForCoach(
  coachUserId: string,
): Promise<TransformationReportWithRespondent[]> {
  const { data } = await db
    .from('transformation_reports')
    .select('*, users!transformation_reports_respondent_user_id_fkey(full_name, email)')
    .eq('coach_user_id', coachUserId)
    .order('created_at', { ascending: false });

  return ((data || []) as any[]).map((r: any) => ({
    ...r,
    respondent_name: r.users?.full_name || r.users?.email || r.respondent_user_id,
    respondent_email: r.users?.email || '',
  }));
}
